import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Crown, CheckCircle2, Zap, TrendingUp, CreditCard, ArrowUpCircle,
  ArrowDownCircle, RotateCcw, ExternalLink, Trophy, Sparkles, Loader2,
  AlertTriangle, Infinity as InfIcon,
} from "lucide-react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/PageShell";

/**
 * /meu-plano · Área do assinante.
 * Consolida: plano atual + próxima cobrança + consumo por franquia + upgrade/downgrade + cancelamento.
 */

const NAVY = "#0B1B2F";
const GOLD = "#C3A05A";
const CANONICAL_ORDER = ["estagiario", "junior", "pleno", "senior", "master"];

const fmtBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v || 0));
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return "—"; }
};

export default function MyPlan() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, e] = await Promise.all([
        api.get("/billing/subscriptions/me"),
        api.get("/billing/plans"),
        api.get("/billing/entitlements"),
      ]);
      setSub(s.data);
      const list = (p.data.items || []).slice().sort((a, b) => {
        const ao = a.order ?? CANONICAL_ORDER.indexOf(a.code);
        const bo = b.order ?? CANONICAL_ORDER.indexOf(b.code);
        return ao - bo;
      });
      setPlans(list);
      setEntitlements((e.data?.items || []).filter(x => x.status === "ACTIVE"));
    } catch (err) {
      toast.error("Falha ao carregar dados do plano");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const currentCode = sub?.subscription?.plan_code || sub?.current_plan?.code || "estagiario";
  const currentPlan = plans.find(p => p.code === currentCode);
  const currentIdx  = CANONICAL_ORDER.indexOf(currentCode);
  const nextUp      = plans.find(p => CANONICAL_ORDER.indexOf(p.code) === currentIdx + 1);
  const nextDown    = plans.find(p => CANONICAL_ORDER.indexOf(p.code) === currentIdx - 1);

  // Entitlements do plano atual apenas
  const myEnts = useMemo(
    () => entitlements.filter(e => e.plan_code === currentCode),
    [entitlements, currentCode]
  );

  const hasStripeSub = !!sub?.subscription?.stripe_subscription_id;
  const priceMonthly = Number(currentPlan?.price_monthly || 0);
  const nextChargeAt = sub?.subscription?.current_period_end || null;
  const cancelAtEnd  = !!sub?.subscription?.cancel_at_period_end;

  const change = async (targetCode) => {
    setBusy(true);
    try {
      if (hasStripeSub) {
        await api.post("/billing/subscriptions/change-plan", { plan_code: targetCode, proration: true });
        toast.success(CANONICAL_ORDER.indexOf(targetCode) > currentIdx
          ? "Upgrade aplicado com proration"
          : "Downgrade agendado para o fim do ciclo");
        await load();
        return;
      }
      const r = await api.post("/billing/subscriptions/checkout",
        { plan_code: targetCode, origin_url: window.location.origin });
      if (r.data?.checkout_url) window.location.href = r.data.checkout_url;
      else await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao alterar plano"); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!hasStripeSub) return;
    if (!window.confirm("Cancelar assinatura ao fim do ciclo atual?")) return;
    setBusy(true);
    try {
      await api.post("/billing/subscriptions/cancel");
      toast.success("Cancelamento agendado para o fim do ciclo");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao cancelar"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="my-plan-page">
      <TopBar/>
      <main className="flex-1 p-3 sm:p-6 lg:p-8 font-sans" style={{ color: NAVY }}>
        <div className="mb-6">
          <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold" style={{ color: GOLD }}>
            Assinatura Hermes
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl mt-1" style={{ color: NAVY }}>
            Meu Plano
          </h1>
          <p className="text-slate-600 text-sm mt-1">
            Consumo, próxima cobrança e mudanças de plano em um só lugar.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400"/></div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6">
            {/* COLUNA ESQUERDA */}
            <div className="space-y-6 min-w-0">
              {/* CARD PLANO ATUAL */}
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 relative overflow-hidden"
                   data-testid="my-plan-current">
                <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10 -mr-16 -mt-16"
                     style={{ background: `radial-gradient(circle, ${GOLD}, transparent)` }}/>
                <div className="relative">
                  <div className="flex items-center gap-2">
                    <Crown className="w-5 h-5" style={{ color: GOLD }}/>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                      Plano atual
                    </div>
                    {cancelAtEnd && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">
                        CANCELA AO FIM DO CICLO
                      </span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-3 mt-2">
                    <h2 className="font-heading text-3xl font-semibold" style={{ color: NAVY }}
                        data-testid="my-plan-name">
                      {currentPlan?.name || currentCode}
                    </h2>
                    <div className="text-slate-500">
                      {priceMonthly === 0 ? "Grátis" : (
                        <span><span className="font-semibold text-slate-800">{fmtBRL(priceMonthly)}</span> /mês</span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                    <MiniStat label="Próxima cobrança" value={hasStripeSub ? fmtDate(nextChargeAt) : "—"}/>
                    <MiniStat label="Método de pagamento" value={hasStripeSub ? "Cartão via Stripe" : "Sem assinatura Stripe"}/>
                    <MiniStat label="Arena OAB" value={currentPlan?.arena_included ? "Incluída" : "Não incluída"}
                              icon={currentPlan?.arena_included ? Trophy : null}
                              iconColor="#F59E0B"/>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-5">
                    {nextUp && (
                      <button onClick={() => change(nextUp.code)} disabled={busy}
                        data-testid="my-plan-upgrade"
                        className="inline-flex items-center gap-2 px-4 h-11 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                        style={{ background: GOLD }}>
                        <ArrowUpCircle className="w-4 h-4"/>
                        Upgrade para {nextUp.name} ({fmtBRL(nextUp.price_monthly)}/mês)
                      </button>
                    )}
                    {nextDown && (
                      <button onClick={() => change(nextDown.code)} disabled={busy}
                        data-testid="my-plan-downgrade"
                        className="inline-flex items-center gap-2 px-4 h-11 rounded-lg text-sm font-medium border border-slate-300 hover:bg-slate-50 disabled:opacity-60"
                        style={{ color: NAVY }}>
                        <ArrowDownCircle className="w-4 h-4"/>
                        Downgrade para {nextDown.name}
                      </button>
                    )}
                    <Link to="/plans" data-testid="my-plan-see-all"
                          className="inline-flex items-center gap-2 px-4 h-11 rounded-lg text-sm font-medium border border-slate-300 hover:bg-slate-50"
                          style={{ color: NAVY }}>
                      <ExternalLink className="w-4 h-4"/> Comparar todos os planos
                    </Link>
                    {hasStripeSub && !cancelAtEnd && (
                      <button onClick={cancel} disabled={busy}
                        data-testid="my-plan-cancel"
                        className="ml-auto inline-flex items-center gap-2 px-4 h-11 rounded-lg text-sm border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-60">
                        <RotateCcw className="w-4 h-4"/> Cancelar
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* CONSUMO POR FRANQUIA */}
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6" data-testid="my-plan-usage">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                      Consumo do ciclo
                    </div>
                    <h2 className="font-heading text-xl mt-0.5" style={{ color: NAVY }}>
                      Suas franquias mensais
                    </h2>
                  </div>
                  <span className="text-xs text-slate-500">renovadas ao fechar o ciclo</span>
                </div>
                {myEnts.length === 0 ? (
                  <div className="text-sm text-slate-400 py-6 text-center">
                    Nenhuma franquia neste plano.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {myEnts.map(e => <UsageRow key={e.service_code} e={e}/>)}
                  </div>
                )}
              </div>
            </div>

            {/* COLUNA DIREITA · resumo mensal */}
            <aside className="space-y-6">
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5" data-testid="my-plan-summary">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4" style={{ color: GOLD }}/>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Este ciclo</div>
                </div>
                <MiniRow label="Mensalidade" value={priceMonthly === 0 ? "Grátis" : fmtBRL(priceMonthly)}/>
                <MiniRow label="Franquias ativas" value={myEnts.length}/>
                <MiniRow label="Serviços ilimitados"
                         value={myEnts.filter(e => e.unlimited).length}/>
                <MiniRow label="Consumo total (mês)"
                         value={myEnts.reduce((s, e) => s + (e.consumed || 0), 0)}/>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <Link to="/finance" data-testid="my-plan-wallet"
                        className="text-xs text-slate-600 hover:text-slate-900 underline decoration-dotted">
                    Ver carteira e histórico de cobranças
                  </Link>
                </div>
              </div>

              {nextUp && (
                <div className="rounded-2xl border-2 border-dashed border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 p-5"
                     data-testid="my-plan-upsell">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-amber-700"/>
                    <div className="text-[10px] uppercase tracking-widest text-amber-800 font-bold">
                      Hermes recomenda
                    </div>
                  </div>
                  <div className="font-heading text-lg text-amber-900">
                    Considere o plano <b>{nextUp.name}</b>
                  </div>
                  <div className="text-xs text-amber-800 mt-1 leading-relaxed">
                    {nextUp.arena_included && !currentPlan?.arena_included && "Arena OAB inclusa · "}
                    Mais franquias e proteção contra excedente.
                  </div>
                  <button onClick={() => change(nextUp.code)} disabled={busy}
                    data-testid="my-plan-upsell-cta"
                    className="mt-3 w-full h-10 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                    style={{ background: GOLD }}>
                    Fazer upgrade agora
                  </button>
                </div>
              )}
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}

function MiniStat({ label, value, icon: Icon, iconColor }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="text-sm font-medium mt-0.5 inline-flex items-center gap-1.5" style={{ color: NAVY }}>
        {Icon && <Icon className="w-4 h-4" style={{ color: iconColor }}/>}
        {value}
      </div>
    </div>
  );
}

function MiniRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm py-1.5">
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold" style={{ color: NAVY }}>{value}</span>
    </div>
  );
}

function UsageRow({ e }) {
  const included = e.unlimited ? Infinity : (e.included || 0);
  const consumed = e.consumed || 0;
  const remaining = e.unlimited ? Infinity : Math.max(0, included - consumed);
  const pct = included === Infinity || included === 0 ? 0 : Math.min(100, (consumed / included) * 100);
  const color = pct >= 90 ? "#DC2626" : pct >= 70 ? "#F59E0B" : "#10B981";
  return (
    <div className="rounded-xl border border-slate-200 p-3 hover:border-slate-300 transition"
         data-testid={`usage-${e.service_code}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[13px] font-medium" style={{ color: NAVY }}>{e.service_name}</div>
        {e.unlimited ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">
            <InfIcon className="w-3 h-3"/> ILIMITADO
          </span>
        ) : (
          <span className="text-[11px] font-mono text-slate-600">
            {consumed} <span className="text-slate-400">/ {included}</span>
          </span>
        )}
      </div>
      {!e.unlimited && (
        <div className="h-1.5 rounded bg-slate-100 overflow-hidden">
          <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }}/>
        </div>
      )}
      <div className="flex items-center justify-between mt-1.5 text-[10.5px] text-slate-500">
        <span>excedente: {fmtBRL(e.overage_price)}/{e.unit}</span>
        {!e.unlimited && remaining <= 0 && (
          <span className="text-rose-600 font-semibold inline-flex items-center gap-0.5">
            <AlertTriangle className="w-3 h-3"/> franquia esgotada
          </span>
        )}
      </div>
    </div>
  );
}
